<style scoped>
.modal-content {
    width: min(1200px, 90vw);
}
</style>
<template>
    <div class="modal-content" ref="container">
        <div class="modal-header">
            <h2 class="modal-title">New User</h2>
            <button type="button" aria-label="Close" class="close icon icon-close" @click.prevent="cancel" />
        </div>
        <div class="modal-body" style="max-height: calc(100vh - 175px); overflow-y: auto">
            <form>
                <div class="form-row">
                    <div class="form-group col-md-6">
                        <label class="control-label" for="userCreateFirst">First</label>
                        <input type="type" id="userCreateFirst" class="form-control" v-model="user.FirstName" />
                    </div>
                    <div class="form-group col-md-6">
                        <label class="control-label" for="userCreateLast">Last</label>
                        <input type="type" id="userCreateLast" class="form-control" v-model="user.LastName" />
                    </div>
                </div>
                <div class="form-group">
                    <label class="control-label dfRequired" for="userCreateEmail">Email</label>
                    <input type="type" id="userCreateEmail" class="form-control" v-model="user.Email" />
                </div>
                <div class="form-group">
                    <label class="control-label" for="userCreateMessage">Message</label>
                    <!-- <input type="type" id="userCreateMessage" class="form-control" v-model="user.Message" /> -->
                    <textarea id="userCreateMessage" class="form-control" v-model="user.InviteMessage"></textarea>
                </div>
                <div class="form-group">
                    <label class="control-label" for="groups">Group</label>
                    <GroupNameList class="p-0 form-group" type="groups" :containerCodeData="containerCodeData" v-on:input="setValue('Groups', $event)" :schema="user" :value="user.Groups" :id="'groups'" :enabled="true" :show-label="false" :errors="[]" />
                </div>
            </form>
            <ul class="list-group list-group-flush role-editor-list">
                <RoleContainer :container="null" :root-code="containerData.ContainerCode" :depth="0" />
            </ul>
        </div>
        <div class="modal-footer">
            <span v-show="noSelectedRoles" class="text-muted font-italic text-smaller mr-3">You must add at least one role.</span>
            <button type="button" class="btn btn-primary" @click.prevent="save" :disabled="noSelectedRoles">Send Invite</button>
            <button type="button" class="btn btn-bare" @click.prevent="cancel">Cancel</button>
            <AsyncOperationStatus :promise="savingPromise" />
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, computed, provide, SetupContext } from "vue";
import { throwIfNonSuccessStatus, fetchXsrf } from "@/lib/fetchHelpers.3";
import RoleContainer from "./RoleContainer.3.vue";
import AsyncOperationStatus from "@/lib/AsyncOperationStatus.3.vue";
import { userModalSetup, UserModalProps, GroupedRole, RoleDtoExtended } from "./UserModalMixin";
import { dialogSetup, DialogProps } from "@/lib/dialogMixin.3";
import GroupNameList from "./GroupNameList.3.vue";
import { useMainStore } from "@/mainStore";
import { UserRoleDto } from "@cs/UserRoleDto";
import { activeContainerRolesInjectKey, implicitContainerRolesInjectKey, possibleContainerRolesInjectKey, getContainerLabelInjectKey, getRoleDisplayLabelInjectKey, setContainerRoleNamesInjectKey } from "./Users.3.vue.InjectKeys";
type Props = UserModalProps & DialogProps;
type User = {
    FirstName: string;
    LastName: string;
    Email: string;
    InviteMessage: string;
    Roles: UserRoleDto[];
    Groups: { label: string; value: string }[];
};

export default defineComponent({
    components: {
        RoleContainer,
        AsyncOperationStatus,
        GroupNameList,
    },
    props: ["userInput", "containerData", "savingCallback", "triggerElement"],
    setup(props: Props, { emit }: SetupContext) {
        const containerRef = ref<HTMLElement>(null!);
        dialogSetup(containerRef, props);
        const userModalMixin = userModalSetup(props);
        const mainStore = useMainStore();

        const user = ref<User>({
            FirstName: null!,
            LastName: null!,
            Email: null!,
            InviteMessage: null!,
            Roles: [],
            Groups: [],
        });
        const savingPromise = ref<Promise<void>>(null!);

        const activeContainerRoles = computed(() => {
            return user.value.Roles.map(i => <GroupedRole>{ Container: i.ContainerCode, ContainerLabel: userModalMixin.getContainerName(i.ContainerCode), RoleName: i.RoleName! });
        });
        const noSelectedRoles = computed(() => {
            return user.value.Roles!.length == 0 && user.value.Groups.length == 0;
        });
        const groups = computed(() => {
            var groups = props.containerData.Roles.filter(i => i.Type == 2).map(k => ({ value: k.Name, label: k.Name }));
            return groups;
        });
        const schema = computed(() => {
            return { ...user.value, Label: "Group" };
        });
        const containerCodeData = computed(() => {
            return props.containerData.ContainerCode;
        });

        const addRole = async ({ container, role }: { container: string; role: string }) => {
            const index = user.value.Roles!.findIndex(i => i.ContainerCode == container && i.RoleName == role);
            if (index < 0) {
                user.value.Roles.push({ ContainerCode: container, RoleName: role });
            }
        };
        // const removeRole = async({ container, role }: { container: string; role: string }) => {
        //     const index = user.value.Roles.findIndex(i => i.ContainerCode == container && i.RoleName == role);
        //     if (index >= 0) {
        //         user.value.Roles.splice(index, 1);
        //     }
        // };
        const save = async () => {
            await (savingPromise.value = (async () => {
                if (!user.value.Email) {
                    alert("You must add an email.");
                    throw "No Email Provided";
                }
                if (user.value.Roles.length == 0 && user.value.Groups.length == 0) {
                    alert("You must add at least one role or group.");
                    throw "UserDeleteBlocked";
                }
                if (user.value.Groups.length > 0) {
                    for (const group of user.value.Groups) {
                        user.value.Roles.push({ ContainerCode: props.containerData.ContainerCode, RoleName: group.value });
                    }
                }

                const response = await fetchXsrf(mainStore, await mainStore.getUrl("adminUserManagementUsersNewFederation"), {
                    method: "POST",
                    credentials: "same-origin",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(user.value),
                });
                throwIfNonSuccessStatus(response);
                props.savingCallback(await response.json());
                emit("close");
            })());
        };
        const cancel = () => {
            if (confirm("Are you sure you want to cancel?")) {
                emit("close");
            }
        };
        //eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO
        const setValue = (prop: string, value: any) => {
            //@ts-expect-error
            user.value[prop] = value;
        };

        provide(activeContainerRolesInjectKey, code => {
            return activeContainerRoles.value.filter(i => i.Container == code) as RoleDtoExtended[];
        });
        provide(implicitContainerRolesInjectKey, code => {
            return userModalMixin.subroles(activeContainerRoles.value as RoleDtoExtended[])[code] || [];
        });
        provide(possibleContainerRolesInjectKey, code => {
            return userModalMixin.possibleRoles(code);
        });
        provide(getContainerLabelInjectKey, code => {
            return userModalMixin.getContainerName(code);
        });
        provide(getRoleDisplayLabelInjectKey, (code, roleName) => {
            return userModalMixin.getRoleDisplayLabel(code, roleName);
        });
        provide(setContainerRoleNamesInjectKey, (code, roleNames) => {
            user.value.Roles = user.value.Roles.filter(i => i.ContainerCode != code || roleNames.includes(i.RoleName));
            const addingRoles = roleNames.filter(i => !user.value.Roles.some(j => j.ContainerCode == code && j.RoleName == i));
            for (const addingRole of addingRoles) {
                addRole({ container: code, role: addingRole });
            }
            return Promise.resolve();
        });

        return {
            // Refs
            container: containerRef,

            // States
            user,
            savingPromise,

            // Computed
            noSelectedRoles,
            groups,
            schema,
            containerCodeData,

            // Methods
            save,
            cancel,
            setValue,
        };
    },
});
</script>
